/**
 * useEmailVerification Hook
 * Custom hook for email verification and resend email functionality
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  verifyEmail,
  resendVerificationEmail,
} from '../services/api/email-verification'
import type {
  VerifyEmailErrorResponse,
  ResendEmailErrorResponse,
} from '../types/email-verification'

export function useEmailVerification(token: string | null) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  /**
   * Verify email mutation
   */
  const verifyMutation = useMutation({
    mutationFn: () => verifyEmail(token!),
    retry: false, // Don't retry verification
    onSuccess: () => {
      toast.success('Email 驗證成功!')
      // Invalidate verification status cache
      queryClient.invalidateQueries({
        queryKey: ['email-verification', 'status'],
      })
      // Navigate after 2 seconds
      setTimeout(() => navigate('/dashboard'), 2000)
    },
    onError: (error: VerifyEmailErrorResponse) => {
      // Error handling in component
      console.error('Email verification failed:', error)
    },
  })

  /**
   * Resend email mutation
   */
  const resendMutation = useMutation({
    mutationFn: resendVerificationEmail,
    retry: false,
    onSuccess: () => {
      toast.success('驗證郵件已重新發送,請查收')
    },
    onError: (error: ResendEmailErrorResponse) => {
      if (error.code === 'TOO_FREQUENT') {
        const retryAfter = error.retryAfter || 60
        toast.error(`請稍後再試 (剩餘 ${retryAfter} 秒)`)
      } else if (error.code === 'ALREADY_VERIFIED') {
        toast.success('您的 Email 已驗證')
      } else {
        toast.error('發送失敗,請稍後再試')
      }
    },
  })

  return {
    verify: verifyMutation,
    resend: resendMutation,
  }
}
